/**
 * Authentication utilities for Hanko Menu.
 * Handles login/logout redirects via the vdp-auth-management BFF.
 *
 * Config is resolved once at module level — env vars are build-time constants.
 */

import { resolveEnv } from '@/config/environments';
import { getFlag } from '@/utils/featureFlag';
import { broadcastLogout, isRelayConnected } from '@/utils/logoutRelay';
import type { SupportedLocale } from '@/types';

const { appTarget, loginApiUrl } = resolveEnv();

const BROADCAST_DELAY_MS = 150;

/**
 * Initiates login by redirecting to the BFF auth endpoint.
 * The returnTo parameter is set to the current page URL so the user
 * lands back where they were after successful authentication.
 *
 * The locale is forwarded as `ui_locales` so the identity provider renders
 * its login screen in the user's language. This must travel as a URL
 * parameter — the auth origin differs from the host page origin, so it
 * cannot read any language preference from storage.
 *
 * @param uiLocales - Resolved locale ('en' | 'ja') to localize the login screen
 */
export function login(uiLocales?: SupportedLocale): void {
  const returnTo = encodeURIComponent(globalThis.location.href);
  const localeParam = uiLocales ? `&ui_locales=${uiLocales}` : '';
  globalThis.location.href = `${loginApiUrl}/auth/login/${appTarget}?returnTo=${returnTo}${localeParam}`;
}

/**
 * Initiates logout by redirecting to the BFF logout endpoint.
 * When SLO flag is enabled, adds ?federated=true to kill the Auth0 session.
 * When the WS relay is connected, also broadcasts to notify other apps.
 */
export function logout(): void {
  const returnTo = encodeURIComponent(globalThis.location.origin);
  const isSloEnabled = getFlag('enableSingleLogout');
  const federatedParam = isSloEnabled ? '&federated=true' : '';

  // Broadcast to other apps if relay is connected
  if (isSloEnabled && isRelayConnected()) {
    broadcastLogout();
    setTimeout(() => {
      globalThis.location.href = `${loginApiUrl}/auth/logout/${appTarget}?returnTo=${returnTo}${federatedParam}`;
    }, BROADCAST_DELAY_MS);
    return;
  }

  globalThis.location.href = `${loginApiUrl}/auth/logout/${appTarget}?returnTo=${returnTo}${federatedParam}`;
}
